/**
 * Being hit, as the cockpit shows it: the `.TTM`'s own hit overlays
 * (`packages/baker/src/cockpit-hits.ts` — blocks 28-35, two per view) painted
 * over the panel and **left there for the rest of the flight**.
 *
 * ## It persists — recovered from `RB.EXE`, not guessed
 *
 * The mechanism is an 8-byte flag array in `damage.c`'s BSS at
 * **`ds:0x611c … ds:0x6123`**, two flags per view, in the original's own view
 * order (forward, rear, right, left — `view.c`'s dispatch table at
 * `cs:0x12f7`). `view.c`'s `draw_cockpit()` (`0x1a35:0x0d65`) guards each
 * overlay with `cmp BYTE PTR ds:0x61xx,0` before playing its block through
 * `tt_drive.c`'s `tt_play` (`0x2d94:0x047b`):
 *
 * | flags | view | blocks |
 * |---|---|---|
 * | `0x611c`/`0x611d` | forward | 28 / 29 |
 * | `0x611e`/`0x611f` | rear | 34 / 35 |
 * | `0x6120`/`0x6121` | right | 32 / 33 |
 * | `0x6122`/`0x6123` | left | 30 / 31 |
 *
 * That table is what independently confirms the baker's block-to-view
 * assignment, which was derived from the art alone (the left/right mirror
 * pairs) before the binary was read — the two agree exactly.
 *
 * **The flags are set-only.** The single write of zero is a `memset(&bh[0], 0,
 * 8)` at `damage.c:0x0092`, inside `damage_init()`, whose only caller in the
 * whole image is `init.c`'s per-flight simulation init. Every other write is
 * `mov BYTE PTR [bx],1`. An exhaustive scan of the image for those eight
 * addresses finds references in exactly two modules — the writer in `damage.c`
 * and the eight read guards in `view.c` — so there is **no decay timer, no
 * countdown, no per-hit lifetime**. Holes accumulate and stay until the next
 * sortie: the Richthofen-era convention, as it turns out.
 *
 * (Worth recording because it was nearly missed: 70 of the 176 hit blocks are
 * made *entirely* of `BULLETS.BMP`'s frames 0-7, the same small dark streaks
 * the 3D layer draws as rounds in flight. Read as tracer that would be a flash,
 * not damage. Read as what they are here — small dark tears in doped fabric —
 * they are holes, and the binary settles it.)
 *
 * ## Which slot a hit marks
 *
 * `damage.c`'s `add_cockpit_hit()` (`0x1911:0x00f8`) seeds its candidate list
 * from the **two slots belonging to the view the pilot is currently looking
 * through**, falls back to any free slot among all eight only when both of
 * those are taken, and picks among the candidates with the simulation tick
 * counter (`0x26a2:0x08cc`) rather than `random.c` — deliberately, so that
 * splash placement is deterministic under VCR replay. This port keys the pick
 * off the hit ordinal for the same reason: it is monotone, replay-stable, and
 * needs no clock.
 *
 * Attacker bearing never enters, and there is no severity grading: the choice
 * is purely "where were you looking".
 *
 * ## One original bug this port does not reproduce
 *
 * `add_cockpit_hit` tests `bh[i]` but stores `&bh[i+1]` (`0x0115`-`0x0135`,
 * reading like `if (hit[i++] == 0) list[n++] = &hit[i];`). The consequence is
 * severe: for any view, `bh[2v]` is only ever *tested*, never written, so it
 * stays zero forever, so the candidate list is never empty, so the all-eight
 * fallback never runs — and **blocks 28, 30, 32 and 34 are unreachable in the
 * shipped game**. A pilot who only ever looks forward sees exactly one splash
 * (block 29) for an entire flight.
 *
 * This port implements the intent instead: both slots of every view are
 * reachable, eight in all. Reproducing a transcription slip that hides half the
 * shipped art is not fidelity, it is a re-enactment of a bug — and the art it
 * hides is art the artists drew. Flipping back is a one-line change to
 * `candidateSlots` if that judgement is ever revisited.
 */

import type { CockpitDrawOp, CockpitHitOverlays, CockpitHitView } from "@red-opus/baker/schema";
import type { GlanceDirection } from "./keys.ts";

/** Slots per view, and the view each pair belongs to — the original's own `bh[8]` layout (§ module doc). */
export const HIT_SLOTS_PER_VIEW = 2;
export const HIT_SLOT_VIEWS: readonly CockpitHitView[] = ["forward", "back", "right", "left"];
export const HIT_SLOT_COUNT = HIT_SLOT_VIEWS.length * HIT_SLOTS_PER_VIEW;

/**
 * How many rounds have to go in before the cockpit starts taking marks —
 * `damage.c`'s own threshold, converted the only way that survives the two
 * damage models disagreeing.
 *
 * The original gates `add_cockpit_hit` on its damage accumulator having passed
 * **25 units** (`0x047b`). One machine-gun round is worth
 * `kindMultiplier[kind]` of those units: the table at `ds:0x1024` is
 * `{10, 15, 10, 15, 50, 100, 0, 512}` and `aa.c` is known to call
 * `damage(target, kind, 1024)` with kinds 4/5 for its 50 and 100
 * (`packages/engine`'s `FLAK_BURST_DAMAGE_UNITS`), so kinds 0-3 — the gunfire
 * kinds — deliver **10 or 15 units a round** on the same magnitude. 25 units
 * is therefore **two rounds**, and the third is the first that marks.
 *
 * Expressed in rounds rather than as a fraction of the airframe on purpose.
 * The tempting conversion is 25/137 of the aeroplane, since
 * `AIRFRAME_DESTROY_DAMAGE_UNITS` already pins 137 as a wreck — but the two
 * models do not destroy an aeroplane at the same place. The original's wreck
 * is severity 7 on one accumulator; this port's is a failed wing, which
 * arrives with the other four components barely touched. Reading the
 * threshold as a damage *fraction* lands it at roughly seventeen rounds, ten
 * times too late: a headed run in which the player was shot until he was
 * wounded and spinning never once reached it, so the cockpit would have shown
 * nothing during the entire engagement it exists to report. The round count is
 * both closer to the original's numbers and closer to its behaviour.
 *
 * INFERRED in one link only: that `mg.c` passes the same 1024 magnitude `aa.c`
 * does. If it passes something else the count moves, but not the shape —
 * gunfire kinds are 10-15 against flak's 50-100, so a round has always been a
 * small fraction of the gate and the gate has always been a couple of rounds.
 */
export const COCKPIT_HIT_THRESHOLD_ROUNDS = 2;

/**
 * Which of the eight overlay slots are showing, as a bitmask — bit
 * `view * HIT_SLOTS_PER_VIEW + slot`, with `view` indexing `HIT_SLOT_VIEWS`.
 * A bitmask rather than an array because the whole state is 8 bits, it is
 * compared every frame to decide whether to repaint, and it has to be cheap to
 * carry through the scene's change-detection.
 */
export type CockpitHitMarks = number;

export interface CockpitHitState {
  readonly marks: CockpitHitMarks;
  /** The gun-hit count these marks are up to date with — what makes the advance idempotent per round. */
  readonly hits: number;
}

/** An unmarked cockpit — what `damage_init`'s `memset` leaves, and what every sortie starts from. */
export const NO_COCKPIT_HITS: CockpitHitState = { marks: 0, hits: 0 };

function slotIndex(view: CockpitHitView, slot: number): number {
  return HIT_SLOT_VIEWS.indexOf(view) * HIT_SLOTS_PER_VIEW + slot;
}

/** The four views with hit art of their own; `up`/`down` are this port's additions and have none. */
const HIT_VIEW_OF: Readonly<Record<GlanceDirection, CockpitHitView | null>> = {
  forward: "forward",
  left: "left",
  right: "right",
  back: "back",
  up: null,
  down: null,
};

/**
 * The free slots a hit may mark: the current view's two, or — once both of
 * those are taken, or the pilot is looking somewhere the original had no view
 * for — any free slot at all. `damage.c`'s own two-stage candidate list.
 */
function candidateSlots(marks: CockpitHitMarks, glance: GlanceDirection): number[] {
  const view = HIT_VIEW_OF[glance];
  const free = (index: number): boolean => (marks & (1 << index)) === 0;
  if (view) {
    const own = [0, 1].map((slot) => slotIndex(view, slot)).filter(free);
    if (own.length > 0) return own;
  }
  const all: number[] = [];
  for (let index = 0; index < HIT_SLOT_COUNT; index++) if (free(index)) all.push(index);
  return all;
}

export interface CockpitHitInput {
  /**
   * Rounds from *guns* that have struck the player, cumulative. Flak is
   * deliberately excluded: `damage.c` skips `add_cockpit_hit` for damage kinds
   * 4 and 5 (`0x04c7`/`0x04cd`), which `aa.c` is the only source of — archie
   * shakes the aeroplane but never puts holes in the cockpit art. That is why
   * this reads `gunHitsTaken` and not `hitsTaken`.
   */
  readonly gunHitsTaken: number;
  /** Where the pilot is looking, which is the only thing that steers the choice of slot. */
  readonly glance: GlanceDirection;
  /** The Realism Panel's AIRCRAFT MAY BE DAMAGED (`ds:0x7279`); the original draws no overlays at all with it off. */
  readonly damageable: boolean;
}

/**
 * One frame of the cockpit's accumulated damage. Marks one slot per round that
 * has struck since the last call, once the airframe is past the threshold —
 * `damage.c` calls `add_cockpit_hit` once per qualifying round, so a tick that
 * lands three rounds marks three slots.
 *
 * Pure and monotone: `marks` bits are only ever set, never cleared, exactly as
 * the original's flags are. A new sortie starts from `NO_COCKPIT_HITS` rather
 * than clearing here, which is the same shape as `damage_init`'s `memset`.
 */
export function advanceCockpitHits(previous: CockpitHitState, input: CockpitHitInput): CockpitHitState {
  const hits = Math.max(previous.hits, input.gunHitsTaken);
  if (!input.damageable) return { marks: previous.marks, hits };

  let marks = previous.marks;
  for (let ordinal = previous.hits + 1; ordinal <= input.gunHitsTaken; ordinal++) {
    // The first rounds are the ones `damage.c`'s 25-unit gate swallows.
    if (ordinal <= COCKPIT_HIT_THRESHOLD_ROUNDS) continue;
    const candidates = candidateSlots(marks, input.glance);
    if (candidates.length === 0) break;
    // The ordinal stands in for `damage.c`'s simulation-tick pick: deterministic
    // under replay, and varying exactly when a new round lands (§ module doc).
    marks |= 1 << candidates[ordinal % candidates.length]!;
  }
  return { marks, hits };
}

/**
 * Every overlay showing over `glance`, in the script's own order — none, one,
 * or both of that view's pair. Empty for a view with no hit art
 * (`AIRCO`'s forward blocks are both empty) and for `up`/`down`, which have no
 * painted panel at all.
 */
export function hitOverlaysFor(
  overlays: CockpitHitOverlays,
  glance: GlanceDirection,
  marks: CockpitHitMarks,
): readonly (readonly CockpitDrawOp[])[] {
  const view = HIT_VIEW_OF[glance];
  if (!view || marks === 0) return [];
  const layers = overlays[view];
  return layers.filter((_, slot) => (marks & (1 << slotIndex(view, slot))) !== 0);
}
